import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_Margins() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📏 CSS Margins</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <code>margin</code> property in CSS is used to create space around
        elements, outside of any defined borders. Margins are completely
        transparent and do not have background color.
      </p>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`selector {
  margin: value;        /* all sides */
  margin-top: value;    /* top margin */
  margin-right: value;  /* right margin */
  margin-bottom: value; /* bottom margin */
  margin-left: value;   /* left margin */
}`}
        />
      </div>

      {/* Example */}
      <div className="tutorial-section">
        <h2 className="section-title">Example</h2>
        <CodeCopyBlock
          code={`div {
  border: 2px solid #333;
  margin: 30px;
}`}
        />
        <div
          style={{
            border: "2px solid #333",
            margin: "30px",
            padding: "10px",
            background: "#f1f5f9",
          }}
        >
          I have <strong>30px margin</strong> on all sides.
        </div>
      </div>

      {/* Individual Margins */}
      <div className="tutorial-section">
        <h2 className="section-title">Individual Margins</h2>
        <p>
          You can set different margins for each side of an element using{" "}
          <code>margin-top</code>, <code>margin-right</code>,{" "}
          <code>margin-bottom</code>, and <code>margin-left</code>.
        </p>
        <CodeCopyBlock
          code={`div {
  margin-top: 20px;
  margin-right: 40px;
  margin-bottom: 60px;
  margin-left: 80px;
}`}
        />
        <div
          style={{
            border: "2px solid #15803d",
            marginTop: "20px",
            marginRight: "40px",
            marginBottom: "60px",
            marginLeft: "80px",
            padding: "10px",
            background: "#dcfce7",
          }}
        >
          Different margins applied on each side.
        </div>
      </div>

      {/* Margin Shorthand */}
      <div className="tutorial-section">
        <h2 className="section-title">Margin Shorthand</h2>
        <p>
          The <code>margin</code> property can take 1–4 values:
        </p>
        <ul>
          <li>
            <strong>1 value:</strong> all sides equal
          </li>
          <li>
            <strong>2 values:</strong> top & bottom | left & right
          </li>
          <li>
            <strong>3 values:</strong> top | left & right | bottom
          </li>
          <li>
            <strong>4 values:</strong> top | right | bottom | left
          </li>
        </ul>
        <CodeCopyBlock
          code={`div { margin: 20px; }         /* all sides 20px */
div { margin: 10px 30px; }    /* top/bottom 10px, left/right 30px */
div { margin: 5px 20px 15px; } /* top 5px, left/right 20px, bottom 15px */
div { margin: 10px 15px 20px 25px; } /* top | right | bottom | left */`}
        />
      </div>

      {/* Auto Centering */}
      <div className="tutorial-section">
        <h2 className="section-title">Centering with Auto Margins</h2>
        <p>
          By setting <code>margin: auto;</code> on left and right, you can
          center block-level elements horizontally (if they have a fixed width).
        </p>
        <CodeCopyBlock
          code={`div {
  width: 300px;
  margin: 0 auto;  /* centers horizontally */
}`}
        />
        <div
          style={{
            width: "300px",
            margin: "0 auto",
            border: "2px solid #0ea5e9",
            padding: "10px",
            background: "#e0f2fe",
            textAlign: "center",
          }}
        >
          I am centered horizontally with <code>margin: auto;</code>
        </div>
      </div>

      {/* Negative Margins */}
      <div className="tutorial-section">
        <h2 className="section-title">Negative Margins</h2>
        <p>
          Margins can also take <strong>negative values</strong>, which pull
          elements closer than normal.
        </p>
        <CodeCopyBlock
          code={`div {
  margin-top: -20px;
}`}
        />
        <div
          style={{
            border: "2px solid #f59e0b",
            marginTop: "-20px",
            padding: "10px",
            background: "#fef3c7",
          }}
        >
          This box is moved upward using <strong>negative margin</strong>.
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          After margins, you should explore{" "}
          <a href="/css/padding" className="learn-more">
            CSS Padding
          </a>{" "}
          to learn how to create inner space inside elements.
        </p>
      </div>
    </div>
  );
}
